// src/services/otpService.js
// 6-digit OTP verification with NO backend and NO Cloud Functions — the
// security rules on otps/{uid} (see firestore.rules) do the actual
// comparison server-side. The raw code is never readable by any client,
// including the account owner's own — verification works by attempting a
// write that the rules only allow through if the guess matches. A wrong
// guess is rejected by Firestore itself, not by app logic that could be
// bypassed in devtools.
import { doc, setDoc, deleteDoc, updateDoc, increment, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { sendOtpEmail, sendWelcomeEmail } from './email';

const OTP_TTL_MINUTES = 10;

const PURPOSE_COPY = {
  signup: 'confirm your new Elpis account',
  'email-change': 'confirm your new email address',
  'new-device': 'confirm this sign-in',
};

function generateCode() {
  return String(Math.floor(100000 + Math.random() * 900000));
}

function otpRef(uid) {
  return doc(db, 'otps', uid);
}

// `viaWelcomeEmail` lets signup piggyback the code on the existing welcome
// email (one send, not two) instead of needing a third EmailJS template.
export async function requestOtp(uid, purpose, targetEmail, profile, { viaWelcomeEmail = false } = {}) {
  const code = generateCode();
  const expiresAt = Timestamp.fromDate(new Date(Date.now() + OTP_TTL_MINUTES * 60 * 1000));
  // Deleting first (not required by the rules, but tidy) so a stale,
  // already-locked-out doc from a prior request never lingers.
  await deleteDoc(otpRef(uid)).catch(() => {});
  await setDoc(otpRef(uid), {
    code, purpose, targetEmail, expiresAt, attempts: 0, verified: false,
    createdAt: Timestamp.now(),
  });
  const purposeText = PURPOSE_COPY[purpose] || 'verify this action';
  if (viaWelcomeEmail) {
    await sendWelcomeEmail({ ...profile, email: targetEmail }, code, purposeText);
  } else {
    await sendOtpEmail({ ...profile, email: targetEmail }, code, purposeText);
  }
  // Dev convenience only — the client already knows the code (it just
  // generated it above), so logging it here doesn't weaken the actual
  // security model at all. Lets testing proceed before EmailJS templates
  // are wired up with the new {{code}} placeholder.
  console.info(`[otp] ${purpose} code for ${targetEmail}: ${code}`);
  return code;
}

// Resolves true/false — never throws for a wrong guess, so callers don't
// need a try/catch just to show "incorrect code."
export async function verifyOtp(uid, guess) {
  try {
    await updateDoc(otpRef(uid), { verified: true, lastGuess: guess });
    return true;
  } catch {
    // Log the failed attempt server-side (atomic increment, no read
    // needed) — best-effort, a failure here just means the local retry
    // counter in the UI is the only thing tracking attempts that round.
    await updateDoc(otpRef(uid), { attempts: increment(1), lastGuess: guess }).catch(() => {});
    return false;
  }
}

export async function clearOtp(uid) {
  await deleteDoc(otpRef(uid)).catch(() => {});
}
